import { useCallback, useRef, useState } from 'react'
import axios from 'axios'
import { grantApi } from '../lib/api'
import { storeGrantSessionToken } from '../lib/session/grantSessionToken'

export type EnsureSessionOutcome = 'ok' | 'termsRequired' | 'unavailable'

/**
 * Shared attendee/canvasser grant-redeem state machine. Bridges an anonymous magic-link token to
 * a grant_session cookie: `ensureSession()` redeems, surfacing the three outcomes the callers need
 * (ok / termsRequired -> show TermsAckPanel / unavailable -> pre-reseed link with no paired grant).
 * Extracted from useAttendeeSession so the profile and outcome hooks share it (spec §4.5).
 */
export function useGrantRedeem(token: string | undefined) {
  const [termsRequired, setTermsRequired] = useState(false)
  const [termsVersion, setTermsVersion] = useState<string | undefined>(undefined)
  const [unavailable, setUnavailable] = useState(false)
  const [isRedeeming, setIsRedeeming] = useState(false)
  const termsVersionRef = useRef<string | undefined>(undefined)

  const ensureSession = useCallback(
    async (acceptTerms?: boolean): Promise<EnsureSessionOutcome> => {
      setIsRedeeming(true)
      setUnavailable(false)
      try {
        const { data } = await grantApi.redeem({
          token: token ?? '',
          acceptTerms,
          termsVersion: acceptTerms ? termsVersionRef.current : undefined,
        })
        if (data.termsRequired) {
          termsVersionRef.current = data.termsVersion ?? undefined
          setTermsVersion(data.termsVersion ?? undefined)
          setTermsRequired(true)
          return 'termsRequired'
        }
        // [Native B1, NB1-2] A native-origin redeem returns the gs_ in the body (no cookie): store it
        // before 'ok', so every following /lobby/attendee* call carries it. No-op on web.
        await storeGrantSessionToken(data.identity ?? {})
        setTermsRequired(false)
        return 'ok'
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setUnavailable(true)
          return 'unavailable'
        }
        throw err
      } finally {
        setIsRedeeming(false)
      }
    },
    [token],
  )

  return { ensureSession, termsRequired, termsVersion, unavailable, isRedeeming }
}
